import React, { useState, useEffect } from 'react';
import { useUsuarioForm } from '../hooks/useUsuarioForm';
import { FormInput, FormSelect, FormEmailInput } from '../components/form/FormFields';
import FotoUsuarioField from '../components/form/FotoUsuarioField';
import { Rol } from '../types/usuario.types';
import { getConfig } from 'utils/appConfig';
import { catalogStorage } from 'utils/catalogStorage';
import { RolItem } from 'types/usuario.types';
import { reniecService } from 'services/reniecService';

// ============================================================
// UsuarioFormPage – Registro / Modificación (2 pasos)
// ============================================================

interface Props {
  idUsuario?: number;
  rolFijo?:   Rol;
  onSuccess?: () => void;
  onCancel?:  () => void;
}

// Metadatos visuales por nombre de rol (label, descripción, color de tarjeta)
const ROL_META: Record<string, { label: string; desc: string; color: string }> = {
  ADMIN:      { label: 'Administrador',   desc: 'Acceso total al sistema',         color: 'border-red-400 bg-red-50 text-red-800' },
  DIRECTOR:   { label: 'Director',        desc: 'Gestión institucional',           color: 'border-purple-400 bg-purple-50 text-purple-800' },
  SECRETARIA: { label: 'Secretaría',      desc: 'Matrículas y documentación',      color: 'border-pink-400 bg-pink-50 text-pink-800' },
  DOCENTE:    { label: 'Docente',         desc: 'Dicta cursos y registra notas',   color: 'border-blue-400 bg-blue-50 text-blue-800' },
  ESTUDIANTE: { label: 'Estudiante',      desc: 'Accede a sus cursos y notas',     color: 'border-green-400 bg-green-50 text-green-800' },
  PADRE:      { label: 'Padre/Apoderado', desc: 'Seguimiento de sus hijos',        color: 'border-orange-400 bg-orange-50 text-orange-800' },
  AUXILIAR:   { label: 'Auxiliar',        desc: 'Registra asistencia diaria',      color: 'border-teal-400 bg-teal-50 text-teal-800' },
  PSICOLOGO:  { label: 'Psicólogo',       desc: 'Atenciones, derivaciones y casos de convivencia', color: 'border-indigo-400 bg-indigo-50 text-indigo-800' },
};

/** Construye la lista de roles con la siguiente prioridad:
 *  1. Cache del backend en localStorage (fuente de verdad)
 *  2. Roles definidos en config.xml  (fallback configurable)
 *  3. Claves de ROL_META             (último recurso hardcodeado)
 */
function buildRolesRegistro() {
  const fromBackend = catalogStorage.getRoles();
  const fromConfig  = getConfig().roles;

  let items: RolItem[];
  if (fromBackend && fromBackend.length > 0) {
    items = fromBackend;
  } else if (fromConfig.length > 0) {
    items = fromConfig.map(r => ({ idRol: r.idRol, nombre: r.nombre }));
  } else {
    items = Object.keys(ROL_META).map((nombre, i) => ({ idRol: i + 1, nombre }));
  }

  return items.map(r => {
    const meta = ROL_META[r.nombre];
    // El label del config.xml tiene prioridad sobre el de ROL_META cuando
    // venimos del fallback de config (fromConfig), pero si hay meta local se usa.
    const configLabel = fromConfig.find(c => c.nombre === r.nombre)?.label;
    return {
      value: r.nombre as Rol,
      idRol: r.idRol,
      label: meta?.label ?? configLabel ?? r.nombre,
      desc:  meta?.desc  ?? '',
      color: meta?.color ?? 'border-slate-300 bg-slate-50 text-slate-800',
    };
  });
}

const ESTADO_OPS = [
  { value: 'true',  label: 'Activo' },
  { value: 'false', label: 'Inactivo' },
];

const SpinnerIcon = () => (
  <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
  </svg>
);

const UsuarioFormPage: React.FC<Props> = ({ idUsuario, rolFijo, onSuccess = () => {}, onCancel = () => {} }) => {
  const [showPass, setShowPass] = useState(false);
  const isEditing = idUsuario !== undefined;

  const ROLES_REGISTRO = buildRolesRegistro();

  const {
    form, step, errors, submitting, submitError, loadingInit,
    handleChange, handleRolChange, nextStep, prevStep, handleSubmit, loadUsuario,
    prefillReniec,
  } = useUsuarioForm(idUsuario ?? null, onSuccess, rolFijo);

  const [reniecLoading, setReniecLoading] = useState(false);
  const [reniecError,   setReniecError]   = useState<string | null>(null);
  const [reniecOk,      setReniecOk]      = useState(false);

  const handleDniBlur = async () => {
    const dni = form.dni.trim();
    if (dni.length !== 8 || !/^\d{8}$/.test(dni)) return;
    setReniecLoading(true); setReniecError(null); setReniecOk(false);
    try {
      const result = await reniecService.consultarDni(dni);
      if (!result.dniValido) {
        setReniecError('DNI no encontrado o inválido en RENIEC.');
      } else if (result.datos) {
        prefillReniec(
          result.datos.nombres,
          result.datos.apellidoPaterno,
          result.datos.apellidoMaterno,
          result.datos.sexo ?? null,
        );
        setReniecOk(true);
      }
    } catch {
      // RENIEC inaccesible — solo validación de formato, sin bloquear
    } finally {
      setReniecLoading(false);
    }
  };

  const rolInfo = rolFijo ? ROLES_REGISTRO.find(r => r.value === rolFijo) : null;

  useEffect(() => { loadUsuario(); }, [loadUsuario]);

  if (loadingInit) {
    return <div className="flex justify-center items-center h-64"><SpinnerIcon /></div>;
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Cabecera */}
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onCancel}
          className="p-2 rounded-lg text-slate-400 hover:text-blue-700 hover:bg-slate-100 transition-colors">
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
            <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-slate-800">
            {isEditing ? 'Modificar Usuario' : rolFijo ? `Registrar ${rolInfo?.label ?? rolFijo}` : 'Registrar Usuario'}
          </h1>
          <p className="text-slate-400 text-sm">
            {isEditing ? 'Actualiza los datos del usuario' : 'Complete los datos para crear un nuevo usuario'}
          </p>
        </div>
      </div>

      {/* Stepper */}
      <div className="flex items-center mb-8">
        {['Datos Personales', 'Acceso y Rol'].map((label, i) => {
          const n = i + 1;
          const done = step > n; const active = step === n;
          return (
            <React.Fragment key={n}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-all
                  ${done ? 'bg-green-500 text-white' : active ? 'bg-blue-700 text-white ring-4 ring-blue-100' : 'bg-slate-200 text-slate-400'}`}>
                  {done ? (
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                      <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd"/>
                    </svg>
                  ) : n}
                </div>
                <span className={`text-sm font-semibold hidden sm:block
                  ${active ? 'text-blue-700' : done ? 'text-green-600' : 'text-slate-400'}`}>{label}</span>
              </div>
              {i < 1 && <div className={`flex-1 h-0.5 mx-3 ${step > n ? 'bg-green-400' : 'bg-slate-200'}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Tarjeta */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-900 to-blue-700 px-6 py-4">
          <h2 className="text-white font-bold">
            {step === 1 ? 'Paso 1: Datos Personales' : 'Paso 2: Acceso y Rol'}
          </h2>
          <p className="text-blue-200 text-xs mt-0.5">
            {step === 1 ? 'Información de identificación del usuario' : 'Credenciales de acceso y asignación de rol'}
          </p>
        </div>

        <div className="p-6 space-y-4">

          {/* ── PASO 1: Datos personales ── */}
          {step === 1 && (
            <>
              {isEditing && idUsuario && (
                <FotoUsuarioField
                  idUsuario={idUsuario}
                  nombres={form.nombres}
                  apellidos={form.apellidos}
                />
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormInput label="Nombres" name="nombres" value={form.nombres}
                  onChange={handleChange} error={errors.nombres} required placeholder="Ej: María Elena" />
                <FormInput label="Apellidos" name="apellidos" value={form.apellidos}
                  onChange={handleChange} error={errors.apellidos} required placeholder="Ej: García López" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <FormInput label="DNI" name="dni" value={form.dni}
                    onChange={e => { handleChange(e); setReniecError(null); setReniecOk(false); }}
                    onBlur={handleDniBlur}
                    error={errors.dni || reniecError || undefined}
                    required placeholder="8 dígitos" maxLength={8} />
                  {reniecLoading && (
                    <p className="text-xs text-slate-400 mt-1 flex items-center gap-1">
                      <svg className="animate-spin w-3 h-3" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4Z"/>
                      </svg>
                      Consultando RENIEC...
                    </p>
                  )}
                  {reniecOk && !reniecLoading && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <svg viewBox="0 0 16 16" fill="currentColor" className="w-3 h-3">
                        <path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd"/>
                      </svg>
                      Datos pre-cargados desde RENIEC
                    </p>
                  )}
                </div>
                <FormInput label="Fecha de Nacimiento" name="fechaNacimiento" type="date"
                  value={form.fechaNacimiento} onChange={handleChange} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormEmailInput label="Correo Electrónico" name="email" value={form.email}
                  domain={getConfig().correodominio || '@gmail.com'}
                  onChange={handleChange} error={errors.email} required />
                <FormInput label="Teléfono" name="telefono" value={form.telefono}
                  onChange={handleChange} error={errors.telefono} required placeholder="9XXXXXXXX" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormSelect
                  label="Sexo"
                  name="sexo"
                  value={form.sexo}
                  onChange={handleChange}
                  error={errors.sexo}
                  required={form.rol === 'ESTUDIANTE'}
                  options={[
                    { value: '',  label: '— Sin especificar —' },
                    { value: 'M', label: 'Masculino' },
                    { value: 'F', label: 'Femenino'  },
                  ]}
                />
              </div>
              <FormInput label="Dirección" name="direccion" value={form.direccion}
                onChange={handleChange} placeholder="Av. Principal 123, Urb. Las Flores" />
              {form.rol === 'ESTUDIANTE' && (
                <FormInput label="Código SIAGIE" name="codigoSiagie" value={form.codigoSiagie}
                  onChange={handleChange} placeholder="Código SIAGIE del estudiante" />
              )}
            </>
          )}

          {/* ── PASO 2: Acceso y Rol ── */}
          {step === 2 && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Usuario</p>
                  <div className="px-3 py-2.5 rounded-lg border border-slate-200 bg-slate-100 text-sm text-slate-500 font-mono">
                    {form.username || <span className="text-slate-300">Se generará con el DNI</span>}
                  </div>
                </div>
                <FormSelect label="Estado" name="activo" value={String(form.activo)}
                  onChange={handleChange} options={ESTADO_OPS} required />
              </div>

              {/* Selector de rol — bloqueado si rolFijo está definido */}
              <div>
                <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
                  Rol del Usuario <span className="text-red-500">*</span>
                </p>
                {rolFijo && rolInfo ? (
                  <div className={`flex items-center gap-3 p-4 rounded-xl border-2 ${rolInfo.color}`}>
                    <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 opacity-60 flex-shrink-0">
                      <path fillRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clipRule="evenodd"/>
                    </svg>
                    <div>
                      <p className="font-bold text-sm">{rolInfo.label}</p>
                      <p className="text-xs opacity-60">{rolInfo.desc}</p>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {ROLES_REGISTRO.map(r => (
                      <button key={r.value} type="button"
                        onClick={() => handleRolChange(r.value)}
                        className={`text-left p-3 rounded-xl border-2 transition-all duration-150 w-full
                          ${form.rol === r.value
                            ? r.color + ' shadow-sm scale-[1.02]'
                            : 'border-slate-200 bg-white hover:border-slate-300 text-slate-600'
                          }`}>
                        <div className="flex items-center justify-between mb-0.5">
                          <p className="font-bold text-sm">{r.label}</p>
                          {form.rol === r.value && (
                            <div className="w-4 h-4 rounded-full bg-current flex items-center justify-center opacity-60">
                              <svg viewBox="0 0 10 10" className="w-2.5 h-2.5">
                                <path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
                              </svg>
                            </div>
                          )}
                        </div>
                        <p className={`text-xs leading-tight ${form.rol === r.value ? 'opacity-60' : 'text-slate-400'}`}>
                          {r.desc}
                        </p>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Contraseña */}
              <div className="border-t border-slate-100 pt-4 space-y-3">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                  Contraseña de Acceso
                </p>

                {isEditing ? (
                  /* Edición: campo editable pre-relleno con la estructura por defecto */
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                        Contraseña <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <input name="password" type={showPass ? 'text' : 'password'}
                          value={form.password} onChange={handleChange}
                          placeholder="Mínimo 8 caracteres" autoComplete="new-password"
                          className={`w-full px-3 py-2.5 pr-10 rounded-lg border text-sm bg-slate-50 outline-none
                            focus:ring-2 focus:ring-blue-500 transition-all
                            ${errors.password ? 'border-red-400 bg-red-50' : 'border-slate-200'}`} />
                        <button type="button" onClick={() => setShowPass(v => !v)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                          <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                            <path d="M10 12.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z"/>
                            <path fillRule="evenodd" d="M.664 10.59a1.651 1.651 0 0 1 0-1.186A10.004 10.004 0 0 1 10 3c4.257 0 7.893 2.66 9.336 6.41.147.381.146.804 0 1.186A10.004 10.004 0 0 1 10 17c-4.257 0-7.893-2.66-9.336-6.41ZM14 10a4 4 0 1 1-8 0 4 4 0 0 1 8 0Z" clipRule="evenodd"/>
                          </svg>
                        </button>
                      </div>
                      {errors.password && <span className="text-red-500 text-xs">{errors.password}</span>}
                    </div>
                    <FormInput label="Confirmar Contraseña" name="confirmarPassword"
                      type={showPass ? 'text' : 'password'} value={form.confirmarPassword}
                      onChange={handleChange} error={errors.confirmarPassword}
                      placeholder="Repita la contraseña" autoComplete="new-password" required />
                  </div>
                ) : (
                  /* Nuevo: contraseña auto-generada, solo lectura */
                  <div className="flex flex-col gap-1">
                    <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">
                      Contraseña (auto-generada)
                    </label>
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg border border-slate-200
                                    bg-slate-100 text-sm font-mono text-slate-600">
                      <span className="flex-1">
                        {form.password || <span className="text-slate-300 font-sans">Se generará con el rol y DNI</span>}
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-700 font-sans font-semibold
                                       px-2 py-0.5 rounded-full flex-shrink-0">
                        automática
                      </span>
                    </div>
                    <p className="text-xs text-slate-400">
                      Formato: 3 letras del rol + _ + DNI (ej: est_12345678)
                    </p>
                  </div>
                )}

                {isEditing && form.password && <PasswordStrength password={form.password} />}
              </div>
            </>
          )}

          {submitError && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-8-5a.75.75 0 0 1 .75.75v4.5a.75.75 0 0 1-1.5 0v-4.5A.75.75 0 0 1 10 5Zm0 10a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/>
              </svg>
              <span className="text-red-600 text-sm">{submitError}</span>
            </div>
          )}
        </div>

        {/* Footer de navegación */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
          <button onClick={step === 1 ? onCancel : prevStep}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-200 transition-colors">
            <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
              <path fillRule="evenodd" d="M11.78 5.22a.75.75 0 0 1 0 1.06L8.06 10l3.72 3.72a.75.75 0 1 1-1.06 1.06l-4.25-4.25a.75.75 0 0 1 0-1.06l4.25-4.25a.75.75 0 0 1 1.06 0Z" clipRule="evenodd"/>
            </svg>
            {step === 1 ? 'Cancelar' : 'Anterior'}
          </button>

          <div className="flex gap-2">
            {[1, 2].map(n => (
              <div key={n} className={`w-2 h-2 rounded-full transition-colors
                ${step === n ? 'bg-blue-600' : step > n ? 'bg-green-400' : 'bg-slate-200'}`} />
            ))}
          </div>

          {step < 2 ? (
            <button onClick={nextStep}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-sm font-semibold bg-blue-700 hover:bg-blue-800 text-white transition-colors shadow-sm">
              Siguiente
              <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                <path fillRule="evenodd" d="M8.22 5.22a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 0 1 0 1.06l-4.25 4.25a.75.75 0 0 1-1.06-1.06L11.94 10 8.22 6.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd"/>
              </svg>
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting}
              className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold bg-green-600 hover:bg-green-700 text-white transition-colors shadow-sm disabled:opacity-60">
              {submitting ? <SpinnerIcon /> : (
                <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
                  <path d="M10.75 2.75a.75.75 0 0 0-1.5 0v8.614L6.295 8.235a.75.75 0 1 0-1.09 1.03l4.25 4.5a.75.75 0 0 0 1.09 0l4.25-4.5a.75.75 0 0 0-1.09-1.03l-2.955 3.129V2.75Z"/>
                  <path d="M3.5 12.75a.75.75 0 0 0-1.5 0v2.5A2.75 2.75 0 0 0 4.75 18h10.5A2.75 2.75 0 0 0 18 15.25v-2.5a.75.75 0 0 0-1.5 0v2.5c0 .69-.56 1.25-1.25 1.25H4.75c-.69 0-1.25-.56-1.25-1.25v-2.5Z"/>
                </svg>
              )}
              {isEditing ? 'Guardar Cambios' : 'Registrar Usuario'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Fortaleza de contraseña ───────────────────────────────────
const PasswordStrength: React.FC<{ password: string }> = ({ password }) => {
  const checks = [
    { label: 'Mínimo 8 caracteres', ok: password.length >= 8 },
    { label: 'Mayúscula',           ok: /[A-Z]/.test(password) },
    { label: 'Minúscula',           ok: /[a-z]/.test(password) },
    { label: 'Número',              ok: /\d/.test(password) },
    { label: 'Carácter especial',   ok: /[^A-Za-z0-9]/.test(password) },
  ];
  const score  = checks.filter(c => c.ok).length;
  const colors = ['', '#f87171', '#fb923c', '#facc15', '#60a5fa', '#22c55e'];
  const labels = ['', 'Muy débil', 'Débil', 'Regular', 'Buena', 'Excelente'];
  return (
    <div className="bg-slate-50 rounded-xl p-3 border border-slate-100">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex gap-1 flex-1">
          {[1,2,3,4,5].map(i => (
            <div key={i} className="h-1.5 flex-1 rounded-full transition-all"
              style={{ background: i <= score ? colors[score] : '#e2e8f0' }} />
          ))}
        </div>
        <span className="text-xs font-bold" style={{ color: colors[score] || '#94a3b8' }}>{labels[score]}</span>
      </div>
      <div className="grid grid-cols-2 gap-1">
        {checks.map(c => (
          <div key={c.label} className="flex items-center gap-1.5">
            <div className={`w-3.5 h-3.5 rounded-full flex items-center justify-center flex-shrink-0 ${c.ok ? 'bg-green-500' : 'bg-slate-200'}`}>
              {c.ok && <svg viewBox="0 0 10 10" className="w-2 h-2"><path d="M2 5l2 2 4-4" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/></svg>}
            </div>
            <span className={`text-xs ${c.ok ? 'text-green-700' : 'text-slate-400'}`}>{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default UsuarioFormPage;
